<template>
  <div class="max-w-4xl w-full mx-auto space-y-6">
    <h1 class="text-2xl font-bold text-gray-800">Snapshot Bulk Upload</h1>
    <div v-if="selected_university" class="mt-1">
      <a :href="snapshotPath" target="_blank" rel="noopener" class="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50">
        Open snapshot page ({{ selected_university.slug }})
      </a>
    </div>

    <!-- University selector -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Select University</label>
      <select v-model="selected_university" @change="onUniChange" class="w-full h-10 px-3 border rounded-lg">
        <option disabled :value="null">Select University</option>
        <option v-for="u in universities" :key="u.id" :value="u">{{ u.name }}</option>
      </select>
    </div>

    <!-- Ceremony selector -->
    <div v-if="selected_university">
      <label class="block text-sm font-medium text-gray-700 mb-1">Select Ceremony</label>
      <select v-model="selected_ceremony" class="w-full h-10 px-3 border rounded-lg">
        <option disabled :value="null">Select ceremony</option>
        <option v-for="c in ceremonies" :key="c.id" :value="c.id">
          {{ formatCeremonyOption(c) }}
        </option>
      </select>
    </div>

    <!-- Dropzone -->
    <div v-if="selected_ceremony">
      <div v-if="!ui.dropzoneCollapsed"
        class="relative border-2 border-dashed rounded-lg p-8 text-center bg-white"
        @dragover.prevent
        @dragenter.prevent
        @drop.prevent="onDrop"
      >
        <button type="button" @click="ui.dropzoneCollapsed = true" class="absolute top-2 right-2 text-xs text-gray-500 hover:text-gray-700">Collapse</button>
        <p class="text-gray-700">Drag and drop images or folders here</p>
        <div class="text-xs text-gray-500">Folders (with nested subfolders) are supported.</div>
        <div class="mt-3">
          <label class="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 cursor-pointer">
            <span>Select files or a folder…</span>
            <input type="file" multiple webkitdirectory directory accept="image/*" @change="onFileChange" class="hidden" />
          </label>
        </div>
        <div v-if="collecting" class="mt-3 text-sm text-gray-600">
          Processing files… Found {{ collectStats.found }}
        </div>
      </div>
      <div v-else class="bg-white border rounded-lg p-3 flex items-center justify-between text-sm">
        <div class="text-gray-700">Upload area collapsed.</div>
        <div class="space-x-2">
          <button type="button" @click="ui.dropzoneCollapsed = false" class="px-2 py-1 rounded border bg-white hover:bg-gray-50">Expand</button>
        </div>
      </div>
    </div>

    <!-- Files table / overall progress -->
    <div v-if="files.length">
      <div class="flex justify-between items-center">
        <div class="w-full">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold">{{ files.length }} file(s) ready</h2>
            <div class="text-xs text-gray-600">{{ doneCount }} done</div>
          </div>
          <!-- Overall progress bar -->
          <div class="mt-2">
            <div class="w-full bg-gray-200 rounded-full h-3">
              <div class="bg-blue-600 h-3 rounded-full" :style="{ width: overallPercent + '%' }"></div>
            </div>
            <div class="text-xs text-gray-500 mt-1 flex items-center justify-between">
              <div>
                {{ formatBytes(overall.uploadedBytes) }} / {{ formatBytes(overall.totalBytes) }} ({{ overallPercent.toFixed(0) }}%)
              </div>
              <div class="space-x-4">
                <span v-if="stats.skipped > 0">Skipped: {{ stats.skipped }}</span>
                <span v-if="stats.renamed > 0">Renamed: {{ stats.renamed }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="ml-4 whitespace-nowrap space-x-2">
          <button :disabled="uploading" @click="clearQueue" class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50">Clear</button>
          <button :disabled="uploading" @click="startUpload" class="px-4 py-2 rounded-md border bg-white hover:bg-gray-50">
            {{ uploading ? 'Uploading…' : 'Start Upload' }}
          </button>
          <button v-if="!ui.listCollapsed" @click="ui.listCollapsed = true" class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50">Hide list</button>
          <button v-else @click="ui.listCollapsed = false" class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50">Show list</button>
        </div>
      </div>

      <ul v-show="!ui.listCollapsed" class="divide-y bg-white rounded-md border mt-2 max-h-[60vh] overflow-auto">
        <li v-for="f in files" :key="f.id" class="p-3 flex items-center justify-between">
          <div class="truncate w-1/2 pr-2">
            <div class="font-medium truncate flex items-center space-x-2">
              <span class="truncate" :title="f.finalName">{{ f.finalName }}</span>
              <span v-if="f._renamed" class="text-[10px] rounded px-1 py-0.5 bg-yellow-100 text-yellow-700 border border-yellow-300">renamed</span>
            </div>
            <div class="text-xs text-gray-500 truncate" :title="f.relPath || f.file.name">From: {{ f.relPath || f.file.name }}</div>
            <div class="text-xs text-gray-400">{{ (f.file.size/1024/1024).toFixed(2) }} MB • {{ f.mime }}</div>
          </div>
          <div class="w-1/2 ml-4">
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="bg-blue-600 h-2 rounded-full" :style="{ width: (f.progress||0) + '%' }"></div>
            </div>
            <div class="text-xs text-gray-500 mt-1">{{ f.status }}</div>
          </div>
        </li>
      </ul>
    </div>

    <div v-if="doneCount>0 && doneCount===files.length" class="p-3 rounded bg-green-50 text-green-700">
      <div class="flex items-center justify-between">
        <span>All uploads completed.</span>
        <a :href="snapshotPath" target="_blank" rel="noopener" class="underline font-medium">View snapshots page</a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SnapshotBulkUpload',
  data() {
    return {
      universities: [],
      selected_university: null,
      ceremonies: [],
      selected_ceremony: null,
      files: [],
      stats: { skipped: 0, renamed: 0 },
      overall: { totalBytes: 0, uploadedBytes: 0 },
      ui: { dropzoneCollapsed: false, listCollapsed: false },
      collecting: false,
      collectStats: { found: 0 },
      uploading: false,
      concurrency: 8,
      maxRetries: 3,
    }
  },
  computed: {
    doneCount() {
      return this.files.filter(f => f.status === 'done').length
    },
    snapshotPath() {
      return this.selected_university ? `/snapshot/${this.selected_university.slug}` : '#'
    },
    overallPercent() {
      const total = this.overall.totalBytes || 0
      const uploaded = Math.min(this.overall.uploadedBytes || 0, total)
      return total ? Math.max(0, Math.min(100, (uploaded / total) * 100)) : 0
    }
  },
  created() {
    // Tune concurrency based on connection (best-effort)
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      if (conn && conn.effectiveType) {
        if (conn.effectiveType.includes('2g')) this.concurrency = 1
        else if (conn.effectiveType.includes('3g')) this.concurrency = 2
        else this.concurrency = 4
      }
    } catch (e) {console.log('Connection API not available', e)}
    axios.get(`/universities`).then(r => this.universities = r.data)
  },
  methods: {
    onUniChange() {
      this.selected_ceremony = null
      axios.get(`/universities/${this.selected_university.id}/ceremonies`).then(r => this.ceremonies = r.data)
    },
    formatCeremonyOption(c) {
      const formattedDate = new Date(c.datetime).toLocaleDateString('en-GB', {weekday: 'long', year:'numeric', month:'long', day:'numeric', hour:'numeric', minute:'2-digit', hour12: true})
      return c.label ? `${formattedDate} - ${c.label}` : formattedDate
    },
    async onDrop(e) {
      const dt = e.dataTransfer
      // Prefer traversing directories via DataTransferItem API when available
      let collected = []
      this.collecting = true
      this.collectStats.found = 0
      if (dt && dt.items && dt.items.length && dt.items[0].webkitGetAsEntry) {
        collected = await this.collectFromDataTransferItems(dt.items, () => {
          this.collectStats.found++
        })
      } else {
        const files = Array.from(dt.files || [])
        collected = files.map(f => ({ file: f, relPath: f.webkitRelativePath || f.name }))
        this.collectStats.found = collected.length
      }
      await this.addFilesChunked(collected)
      this.collecting = false
      if (this.files.length) this.ui.dropzoneCollapsed = true
    },
    onFileChange(e) {
      const files = Array.from(e.target.files || [])
      const collected = files.map(f => ({ file: f, relPath: f.webkitRelativePath || f.name }))
      this.addFilesChunked(collected).then(() => { if (this.files.length) this.ui.dropzoneCollapsed = true })
      e.target.value = ''
    },
    async collectFromDataTransferItems(items, onFileCb) {
      const results = []
      const promises = []
      const toArray = (list) => Array.prototype.slice.call(list)
      const readDir = async (dirEntry) => {
        const reader = dirEntry.createReader()
        const readAll = async () => {
          return new Promise((resolve) => {
            const entries = []
            const read = () => {
              reader.readEntries(async (batch) => {
                if (!batch.length) return resolve(entries)
                entries.push(...batch)
                read()
              })
            }
            read()
          })
        }
        const entries = await readAll()
        for (const entry of entries) {
          if (entry.isDirectory) await readDir(entry)
          else if (entry.isFile) {
            const p = new Promise((res) => {
              entry.file((file) => {
                const item = { file, relPath: entry.fullPath?.replace(/^\//, '') || file.name }
                results.push(item)
                if (onFileCb) onFileCb(item)
                res()
              }, () => res())
            })
            promises.push(p)
          }
        }
      }
      for (const it of toArray(items)) {
        const entry = it.webkitGetAsEntry()
        if (!entry) continue
        if (entry.isDirectory) await readDir(entry)
        else if (entry.isFile) {
          const p = new Promise((res) => {
            entry.file((file) => { const item = { file, relPath: file.name }; results.push(item); if (onFileCb) onFileCb(item); res() }, () => res())
          })
          promises.push(p)
        }
      }
      await Promise.all(promises)
      return results
    },
    async addFilesChunked(items) {
      const chunkSize = 200
      let skipped = 0
      let renamed = 0
      const existingNames = new Set(this.files.map(f => f.finalName.toLowerCase()))
      let nextId = this.files.length
      for (let i = 0; i < items.length; i += chunkSize) {
        const batch = items.slice(i, i + chunkSize)
        const mapped = []
        for (const it of batch) {
          const file = it.file
          const relPath = it.relPath || file.webkitRelativePath || file.name
          const mime = this.detectMime(file)
          if (!this.isSupportedImage(file, mime)) { skipped++; continue }
          const suggested = this.suggestFileName(file.name, relPath)
          const { finalName, wasRenamed } = this.ensureUniqueName(suggested, existingNames)
          if (wasRenamed) renamed++
          mapped.push({ id: nextId++, file, relPath, finalName, mime, status: 'queued', progress: 0, _renamed: wasRenamed, totalBytes: file.size, _lastFullLoaded: 0, _lastThumbLoaded: 0 })
        }
        if (mapped.length) {
          // Update totals and push in one go to minimize reactivity churn
          this.overall.totalBytes += mapped.reduce((acc, f) => acc + f.file.size, 0)
          this.files = this.files.concat(mapped)
        }
        // Yield to UI thread for responsiveness
        await new Promise(r => requestAnimationFrame(r))
      }
      this.stats.skipped += skipped
      this.stats.renamed += renamed
    },
    clearQueue() {
      this.files = []
      this.stats = { skipped: 0, renamed: 0 }
      this.overall = { totalBytes: 0, uploadedBytes: 0 }
    },
    async startUpload() {
      if (!this.selected_ceremony || this.files.length === 0) return
      this.uploading = true
      try {
        // Queue uploads with limited concurrency; presign is obtained per-file (just-in-time)
        const queue = [...this.files]
        const workers = Array.from({ length: this.concurrency }).map(() => this.worker(queue))
        await Promise.all(workers)
      } catch (e) {
        console.error('Upload failed to start', e)
      } finally {
        this.uploading = false
      }
    },
    async worker(queue) {
      while (queue.length) {
        const item = queue.shift()
        if (!item) break
        await this.uploadOne(item)
      }
    },
    async uploadOne(item) {
      item.status = 'thumbnailing'
      let thumbBlob
      try {
        thumbBlob = await this.makeThumbnail(item.file, 1280)
      } catch (e) {
        console.warn('Thumbnail generation failed; falling back to original', e)
        thumbBlob = item.file
      }
      item.status = 'uploading'
      // Update totals with thumbnail size now that we know it
      const thumbSize = (thumbBlob?.size || 0)
      const totalBytes = item.file.size + thumbSize
      item.totalBytes = totalBytes
      this.overall.totalBytes += thumbSize
      // Reset per-request counters
      item._lastFullLoaded = 0
      item._lastThumbLoaded = 0
      const update = () => {
        const done = item._lastFullLoaded + item._lastThumbLoaded
        item.progress = Math.min(100, Math.floor((done / Math.max(1, totalBytes)) * 100))
      }

      const attemptUpload = async (attempt = 1) => {
        // Ensure we have fresh presigned URLs for this file
        const presign = await this.ensurePresign(item)
        const put = (url, blob, contentType, isThumb = false) => axios.put(url, blob, {
          headers: { 'Content-Type': contentType },
          transformRequest: (data, headers) => { delete headers.common['Authorization']; return data },
          onUploadProgress: (e) => {
            const prev = isThumb ? (item._lastThumbLoaded || 0) : (item._lastFullLoaded || 0)
            const delta = Math.max(0, e.loaded - prev)
            if (isThumb) item._lastThumbLoaded = e.loaded; else item._lastFullLoaded = e.loaded
            this.overall.uploadedBytes += delta
            update()
          }
        })
        try {
          await Promise.all([
            put(presign.fullres.url, item.file, presign.fullres.content_type || 'application/octet-stream', false),
            put(presign.thumbnail.url, thumbBlob, 'image/jpeg', true),
          ])
          return true
        } catch (err) {
          const status = err?.response?.status
          // For 403 (expired signature) or network timeouts, retry with backoff and fresh presign
          if (attempt < this.maxRetries && (!status || status === 403 || status >= 500)) {
            const delay = Math.min(30000, 1000 * Math.pow(2, attempt))
            await new Promise(r => setTimeout(r, delay))
            // Roll back credited progress for this file attempt before retrying
            this.overall.uploadedBytes -= (item._lastFullLoaded || 0) + (item._lastThumbLoaded || 0)
            item._lastFullLoaded = 0
            item._lastThumbLoaded = 0
            return attemptUpload(attempt + 1)
          }
          throw err
        }
      }

      try {
        const ok = await attemptUpload(1)
        if (ok) {
          item.status = 'done'
          item.progress = 100
        }
      } catch (e) {
        console.error('Upload error', e)
        item.status = 'error'
      }
    },
    async ensurePresign(item) {
      if (item._presign && item._presign.filename === item.finalName) return item._presign
      const body = {
        ceremony_id: this.selected_ceremony,
        files: [{ filename: item.finalName, content_type: item.mime, size: item.file.size }]
      }
      const r = await axios.post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/upload/presign`, body)
      const presign = r.data.items && r.data.items[0]
      if (!presign) throw new Error('Failed to obtain presign for file')
      item._presign = presign
      return presign
    },
    formatBytes(bytes) {
      if (!bytes || bytes <= 0) return '0 B'
      const units = ['B','KB','MB','GB','TB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      const val = bytes / Math.pow(1024, i)
      return `${val.toFixed(val < 10 ? 1 : 0)} ${units[i]}`
    },
    detectMime(file) {
      if (file && file.type) return file.type
      const name = (file && file.name) || ''
      const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase()
      if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
      if (ext === 'png') return 'image/png'
      if (ext === 'webp') return 'image/webp'
      return 'application/octet-stream'
    },
    isSupportedImage(file, mime) {
      const okType = mime && (mime.startsWith('image/'))
      const name = (file && file.name) || ''
      const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase()
      const okExt = ['jpg','jpeg','png','webp'].includes(ext)
      return okType && okExt
    },
    sanitizeBaseName(name) {
      // Keep only allowed chars (server enforces similar pattern); preserve extension
      const dot = name.lastIndexOf('.')
      const base = dot > 0 ? name.slice(0, dot) : name
      const ext = dot > 0 ? name.slice(dot) : ''
      const cleanedBase = base.replace(/[^A-Za-z0-9_.\- ]+/g, ' ').replace(/\s+/g, ' ').trim()
      const cleanedExt = ext.replace(/[^A-Za-z0-9.]/g, '')
      let sanitized = cleanedBase || 'image'
      // Enforce max length 200 inc. extension
      const maxLen = 200 - cleanedExt.length
      if (sanitized.length > maxLen) sanitized = sanitized.slice(0, Math.max(0, maxLen))
      return sanitized + cleanedExt
    },
    suggestFileName(originalName, relPath) {
      // Use folder components as a prefix (flattened) to reduce collisions, but no slashes
      const dot = originalName.lastIndexOf('.')
      const ext = dot >= 0 ? originalName.slice(dot) : ''
      const base = dot >= 0 ? originalName.slice(0, dot) : originalName
      const pathOnly = (relPath || '').split('/').slice(0, -1).filter(Boolean)
      // Only include up to last two folders to avoid overly long names
      const prefixParts = pathOnly.slice(-2).map(p => p.replace(/[^A-Za-z0-9_.\- ]+/g, ' ').replace(/\s+/g, ' ').trim()).filter(Boolean)
      const prefixed = (prefixParts.length ? (prefixParts.join('-') + '-') : '') + base
      return this.sanitizeBaseName(prefixed + ext)
    },
    ensureUniqueName(name, existingLowerSet) {
      const dot = name.lastIndexOf('.')
      const ext = dot >= 0 ? name.slice(dot) : ''
      const base = dot >= 0 ? name.slice(0, dot) : name
      let candidate = name
      let suffix = 1
      let wasRenamed = false
      while (existingLowerSet.has(candidate.toLowerCase())) {
        wasRenamed = true
        suffix += 1
        const next = `${base} (${suffix})${ext}`
        candidate = this.sanitizeBaseName(next)
      }
      existingLowerSet.add(candidate.toLowerCase())
      return { finalName: candidate, wasRenamed }
    },
    makeThumbnail(file, maxWidth) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const scale = Math.min(1, maxWidth / img.width)
          const w = Math.floor(img.width * scale)
          const h = Math.floor(img.height * scale)
          const canvas = document.createElement('canvas')
          canvas.width = w; canvas.height = h
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, w, h)
          canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', 0.8)
        }
        img.onerror = (e) => reject(e)
        const reader = new FileReader()
        reader.onload = (e) => { img.src = e.target.result }
        reader.onerror = (e) => reject(e)
        reader.readAsDataURL(file)
      })
    },
  }
}
</script>

<style scoped>
</style>
